import { ASnackbar } from '@adornis/adornis-components/a-snackbar';
import { AdornisRouter } from '@adornis/base/client/a-router';
import { ChemistryLiteElement } from '@adornis/chemistry/chemistry-lite-element';
import { SizeController } from '@adornis/chemistry/controllers/size-controller';
import { css } from '@adornis/chemistry/directives/css';
import { injectAttributesOf } from '@adornis/chemistry/directives/inject-attributes';
import { ElementsFlex } from '@adornis/chemistry/elements/components/x-flex';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design';
import { ElementsDialog } from '@adornis/dialog/x-dialog.js';
import { inputResetStyle } from '@adornis/forms/x-plain-input';
import '@adornis/germanium/components/ge-button';
import '@adornis/germanium/components/ge-card';
import '@adornis/germanium/components/ge-input';
import { germaniumInputToken } from '@adornis/germanium/components/ge-input';
import '@adornis/germanium/components/ge-link';
import '@adornis/user-forms/x-login';
import { ElementsLogin } from '@adornis/user-forms/x-login';
import { WTGUser } from 'db/wtg-user.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { ref } from 'lit/directives/ref.js';

@customElement('wtg-login-form')
export class WTGLoginForm extends ElementsLogin {
  firstUpdated() {
    // append the inputs manually so they are not children of the shadowRoot and therefore accessible by passwordmanagers
    const emailInput = document.createElement('wtg-login-input') as WTGLoginInput;
    emailInput.setAttribute('type', 'email');
    emailInput.inputlabel = 'Email';
    emailInput.setAttribute('placeholder', 'Email');
    emailInput.addEventListener('value-changed', e => (this._email = e.detail.value));
    emailInput.addEventListener('submit', () => this.login());
    const passwordInput = document.createElement('wtg-login-input') as WTGLoginInput;
    passwordInput.setAttribute('type', 'password');
    passwordInput.inputlabel = 'Password';

    passwordInput.setAttribute('placeholder', 'Password');
    passwordInput.addEventListener('value-changed', e => (this._password = e.detail.value));
    passwordInput.addEventListener('submit', () => this.login());
    this._container.appendChild(emailInput);
    this._container.appendChild(passwordInput);
  }

  private _container?: ElementsFlex;

  @state() size = new SizeController(this);

  private loadErrorMessage() {
    if (this.errorMessage.includes('Die Eingaben dürfen nicht leer sein!')) return 'The fields cannot be empty';

    if (this.errorMessage.includes('Fehler beim Einloggen: loginWithPassword(): Username/Email or password is wrong'))
      return 'Username/Email or password is wrong';
    return this.errorMessage;
  }

  public render() {
    return html`
      <x-flex space="md" ${css({ width: '100%' })}>
        <x-flex
          center
          ${css({ height: '100%', padding: this.size.isMobile ? '' : `${this.spacing.lg} ${this.spacing.xl}` })}
        >
          <x-flex space="md" ${css({ width: '100%' })}>
            <x-flex space="md" ${ref(container => (this._container = container as ElementsFlex | undefined))}> </x-flex>
            <x-text tone="error" ${css({ color: this.colors.accent })}> ${this.loadErrorMessage()} </x-text>
            <x-flex space="md">
              <ge-link>
                <ge-text>
                  <a
                    ${css({ color: '#00455d', fontWeight: '600', textDecorationLine: 'underline' })}
                    @click=${async () => {
                      const email = await ElementsDialog.prompt("What's your email address?");
                      try {
                        await WTGUser.requestChangePassword(email)();
                        ASnackbar.show('You will receive an email shortly.');
                        AdornisRouter.goTo('/');
                      } catch (e) {
                        ASnackbar.show(`Error: ${(e as Error).message}`);
                      }
                    }}
                  >
                    Forgot your password?
                  </a>
                </ge-text>
              </ge-link>
              <x-flex
                ?horizontal=${!this.size.isMobile}
                ?crossaxis-start=${this.size.isMobile}
                ?crossaxis-center=${!this.size.isMobile}
                space="xs"
              >
                <ge-text welther primary> Still no account? </ge-text>
                <ge-text>
                  <a href="/signup" ${css({ color: '#00455d', fontWeight: '600', textDecorationLine: 'underline' })}>
                    Register here!
                  </a>
                </ge-text>
              </x-flex>
            </x-flex>

            <x-flex horizontal end>
              <ge-button
                ${css({ boxShadow: 'none' })}
                mode="action"
                big
                @click=${async () => {
                  this.login();
                }}
              >
                Login
              </ge-button>
            </x-flex>
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }
}

@customElement('wtg-login-input')
export class lWTGLoginInput extends ChemistryLiteElement {
  get inputToken() {
    return germaniumInputToken(this);
  }

  private _input?: HTMLInputElement;
  private visible = false;
  public inputlabel = '';

  styles() {
    return [
      inputResetStyle,
      {
        height: 'unset',
        fontSize: this.baseFontSize,
        '--placeholder-color': this.colors?.neutral_800,
        cursor: 'text',

        [`${DesignSystem.prefix}-flex[input-wrapper]`]: {
          ...this.inputToken,
          height: `calc(${this.baseFontSize}px + ${this.inputToken.paddingTop} + ${this.inputToken.paddingBottom} + 2px)`,
        },
        '[visibility]': {
          margin: `-${this.inputToken.paddingTop} -${this.inputToken.paddingRight} -${this.inputToken.paddingBottom} -${this.spacing.sm}`,
          padding: `${this.inputToken.paddingTop} ${this.inputToken.paddingRight} ${this.inputToken.paddingBottom} ${this.spacing.sm}`,
          cursor: 'pointer',
        },
        '[visibility][on] [vis-off]': { display: 'none' },
        '[visibility][off] [vis]': { display: 'none' },
      },
    ];
  }
  public render() {
    return html`
      <ge-flex space="sm">
        <ge-text> ${this.inputlabel} </ge-text>
        <ge-flex input-wrapper horizontal crossaxis-center space="sm" @click=${() => this._input?.focus()}>
          <input
            ${ref(e => (this._input = e as HTMLInputElement))}
            ${injectAttributesOf(this)}
            @input=${(e: Event) =>
              this.dispatchEvent(
                new CustomEvent('value-changed', { detail: { value: (e.currentTarget as HTMLInputElement).value } }),
              )}
            @search=${(e: Event) =>
              this.dispatchEvent(
                new CustomEvent('value-changed', { detail: { value: (e.currentTarget as HTMLInputElement).value } }),
              )}
            @keyup="${(e: KeyboardEvent) => {
              if (e.key === 'Enter')
                return this.dispatchEvent(
                  new CustomEvent('submit', {
                    detail: { value: (e.currentTarget as HTMLInputElement).value },
                    composed: true,
                    bubbles: true,
                  }),
                );
              if (e.key === 'Escape') return this.dispatchEvent(new CustomEvent('escape'));
            }}"
            @blur=${(e: Event) => {
              this.dispatchEvent(
                new CustomEvent('value-picked', { detail: { value: (e.currentTarget as HTMLInputElement).value } }),
              );
            }}
          />
          ${this._visibilityIcon()}
        </ge-flex>
      </ge-flex>
    `;
  }

  private _visibilityIcon() {
    if (this.getAttribute('type') === 'password')
      return html`
        <ge-flex
          visibility
          off
          @click=${(e: MouseEvent) => {
            const element = e.currentTarget as HTMLElement;
            if (!element) return console.warn('click without currentTarget?');
            this.visible = !this.visible;
            if (this.visible) {
              element.removeAttribute('off');
              element.setAttribute('on', '');
              this._input?.setAttribute('type', 'text');
            } else {
              element.removeAttribute('on');
              element.setAttribute('off', '');
              this._input?.setAttribute('type', 'password');
            }
          }}
        >
          <ge-icon ${css({ fontSize: '20px' })} type="material-outlined" vis> visibility_on </ge-icon>
          <ge-icon ${css({ fontSize: '20px' })} type="material-outlined" vis-off> visibility_off </ge-icon>
        </ge-flex>
      `;
    return html`
      <ge-flex>
        <ge-icon ${css({ fontSize: '20px' })} type="material-outlined" vis> mail </ge-icon>
      </ge-flex>
    `;
  }
}
